import {useState} from "react";
import {Button, Grid, MenuItem, Select} from "@mui/material";
import BackendError from "../../BackendError";
import {FormattedMessage, defineMessage} from "react-intl";
import {ThreeDots} from "@dvrt/graphql-widgets";
import {useSaveGroupMembershipMutation} from "@/mutations.generated";
import {ControllingLineState} from "@/api";
import {useDispatch} from "react-redux";
import {showNotification} from "../../../actions";
import {PickupGroupUser} from "../../../api";
import {useAvailablePickupGroupExtensionsQuery} from "./queries.generated";

type Props = {
    groupId: number;
    onSuccess: () => void;
    shouldReloadOptions: boolean;
    disableReload: () => void;
};

const textStyle = {marginTop: "3rem"};

export default ({groupId, onSuccess, shouldReloadOptions, disableReload}: Props) => {
    const [options, setOptions] = useState([]);
    const [numberId, setNumberId] = useState(0);
    const dispatch = useDispatch();
    const [mutate] = useSaveGroupMembershipMutation();

    const query = useAvailablePickupGroupExtensionsQuery({
        variables: {groupId},
        fetchPolicy: "no-cache",
        onCompleted: (data) => initOptions(data.availablePickupGroupUsers),
    });

    function initOptions(extensions: PickupGroupUser[]) {
        const _options = [];

        for (const availableExtension of extensions) {
            _options.push(
                <MenuItem key={availableExtension.numberId} value={availableExtension.numberId}>
                    {`${availableExtension.shortNumber} - ${availableExtension.displayName}`}
                </MenuItem>,
            );
        }
        setOptions(_options);

        if (extensions.length) setNumberId(extensions[0].numberId);
    }

    async function handleAdd() {
        const mutationResponse = await mutate({
            variables: {
                groupId,
                numberId,
                controllingLine: false,
                state: ControllingLineState.Closed,
            },
        });

        if (mutationResponse.data.saveGroupMembership.errors?.length) {
            dispatch(
                showNotification(defineMessage({id: "unexpected_error", defaultMessage: "Unexpected error"}), "error"),
            );
            return;
        }

        dispatch(
            showNotification(defineMessage({id: "save_success", defaultMessage: "Data successfully saved"}), "success"),
        );
        onSuccess();

        await reloadOptions();
    }

    async function reloadOptions() {
        const queryResponse = await query.refetch();
        initOptions(queryResponse.data.availablePickupGroupUsers);
    }

    if (shouldReloadOptions) {
        reloadOptions();
        disableReload();
    }

    if (query.error)
        return (
            <div style={{padding: 20}}>
                <BackendError error={query.error} />
            </div>
        );

    if (query.loading)
        return (
            <div style={{padding: 20}}>
                <ThreeDots height={5} />
            </div>
        );

    if (!options.length) {
        return (
            <div style={textStyle}>
                <FormattedMessage
                    id="no_available_pickup_group_extension"
                    defaultMessage="No available pickup group user to assign."
                />
            </div>
        );
    }

    return (
        <Grid container spacing={2} style={textStyle}>
            <Grid>
                <Select
                    variant="standard"
                    required
                    fullWidth
                    margin="dense"
                    value={numberId}
                    onChange={(event) => setNumberId(Number(event.target.value))}
                >
                    {options}
                </Select>
            </Grid>
            <Grid>
                <Button size="small" variant="contained" color="primary" onClick={handleAdd}>
                    <FormattedMessage id="add_extension" defaultMessage="Add user" />
                </Button>
            </Grid>
        </Grid>
    );
};
